/*!
* BFCM Tiered Gifts — Drop-in (event-driven, no polling, no empty /?sections=)
* Auto add/remove highest eligible gift without flicker
* Muc-Off | v1.3 | 2025-10-06
*/
(function () {
  'use strict';

  // ─────────────────────────────────────────────────────────────────────────────
  // 1) CONFIG — EDIT ME (if needed)
  // ─────────────────────────────────────────────────────────────────────────────
  // - Provide variantId OR handle per tier.
  // - Thresholds are in minor units (GBP pence): 5000 = £50.00
  // - basis: 'total' (cart.total_price) or 'subtotal' (cart.items_subtotal_price)
  // - sections: DOM ids (or data-section-id) you actually want re-rendered.
  //             Leave [] to disable. DO NOT leave [''].
  window.BFCM_GIFTS_CONFIG = Object.assign({
    campaignKey: 'bfcm-2025',
    basis: 'total',
    currency: 'GBP',
    tiers: [
      // Example — fill these with your real values:
      { key:'tier1', threshold:  5000, variantId: 56296208236917, handle:'free-nano-tech',      label:'Free Nano Tech' },
      { key:'tier2', threshold: 10000, variantId: 56296486502773, handle:'free-concentrate',    label:'Free Concentrate' },
      // { key:'tier3', threshold: 15000, variantId: 33333333333333, handle:'free-5l-concentrate', label:'Free 5L Concentrate' },
    ],
    sections: [],                 // e.g. ['cart-icon-bubble','cart-black-friday-free-gift-message']
    giftProp: '_bfcm_gift',
    enforceGiftGuard: true,       // keep gift qty at 1
    excludeGiftsInAmount: true,   // exclude gift line prices when computing thresholds
    maxSectionRefreshRateMs: 15000 // hard throttle for sections refresh
  }, window.BFCM_GIFTS_CONFIG || {});

  const C = window.BFCM_GIFTS_CONFIG;
  if (!Array.isArray(C.tiers) || C.tiers.length === 0) {
    console.warn('[BFCM] No tiers configured; aborting');
    return;
  }

  // Sanitize/normalize sections: remove blanks/duplicates
  const SECTIONS = Array.from(
    new Set(
      (C.sections || [])
        .map(s => (s == null ? '' : String(s).trim()))
        .filter(Boolean)
    )
  );

  // sort tiers ascending by threshold
  const TIERS = [...C.tiers].sort((a,b) => a.threshold - b.threshold);
  const DEBUG = !!window.BFCM_DEBUG;
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  // backoff map to avoid re-adding failing gifts too often
  const addFailBackoff = new Map();
  const BACKOFF_MS = 60_000;

  // section refresh throttle state
  let lastSectionsSig = '';
  let lastSectionsAt = 0;

  // sync coalescing
  let syncing = false;
  let queued = false;
  let lastSig = null;       // last cart signature
  let lastTierKey = null;   // last computed tier key
  let initPromise = null;

  function log(...a){ if (DEBUG) console.log('[BFCM]', ...a); }

  // ─────────────────────────────────────────────────────────────────────────────
  // 2) UTILITIES
  // ─────────────────────────────────────────────────────────────────────────────
  async function getCart() {
    const r = await fetch('/cart.js', { credentials: 'same-origin', headers:{'Accept':'application/json'} });
    if (!r.ok) throw new Error('cart.js failed');
    return r.json();
  }

  function cartSignature(cart) {
    return cart && (cart.token || `${cart.item_count||0}:${cart.total_price||0}`);
  }

  function amountForBasis(cart) {
    let amt = (C.basis === 'subtotal' ? (cart.items_subtotal_price || 0) : (cart.total_price || 0));
    if (C.excludeGiftsInAmount !== false && cart && Array.isArray(cart.items)) {
      const giftIds = new Set(TIERS.map(t => String(t.variantId)));
      for (const li of cart.items) {
        const isGift = (li.properties && li.properties[C.giftProp] === '1') || giftIds.has(String(li.variant_id));
        if (isGift) {
          const line = (typeof li.final_line_price === 'number') ? li.final_line_price : (li.line_price || 0);
          amt -= line;
        }
      }
      if (amt < 0) amt = 0;
    }
    return amt;
  }

  function eligibleTier(amount) {
    let chosen = null;
    for (const t of TIERS) { if (amount >= t.threshold) chosen = t; else break; }
    return chosen;
  }

  async function resolveVariantIdByHandle(handle) {
    const r = await fetch(`/products/${handle}.js`, { credentials: 'same-origin', headers:{'Accept':'application/json'} });
    if (!r.ok) throw new Error(`Product not found: ${handle}`);
    const p = await r.json();
    const v = (p.variants || []).find(x => x.available) || (p.variants || [])[0];
    if (!v || !v.id) throw new Error(`No variant id for: ${handle}`);
    return String(v.id);
  }

  async function normalizeTiers() {
    await Promise.all(TIERS.map(async t => {
      if (!t.variantId) {
        if (!t.handle) throw new Error(`Tier ${t.key || t.threshold} missing handle & variantId`);
        t.variantId = await resolveVariantIdByHandle(t.handle);
      } else {
        t.variantId = String(t.variantId).split('/').pop(); // strip gid:// if present
      }
    }));
  }

  function findGiftLines(cart) {
    const giftIds = new Set(TIERS.map(t => String(t.variantId)));
    const lines = [];
    for (const li of (cart.items || [])) {
      const isGift = (li.properties && li.properties[C.giftProp] === '1') || giftIds.has(String(li.variant_id));
      if (isGift) lines.push(li);
    }
    return lines;
  }

  async function changeLine(lineKey, quantity) {
    const r = await fetch('/cart/change.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept':'application/json' },
      body: JSON.stringify({ id: lineKey, quantity })
    });
    if (!r.ok) throw new Error(`change failed ${r.status}`);
    return r.json();
  }

  async function addGift(variantId, key) {
    const now = Date.now();
    const lastFail = addFailBackoff.get(variantId) || 0;
    if (now - lastFail < BACKOFF_MS) { log('add backoff active for', variantId); return null; }

    let r = await fetch('/cart/add.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept':'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity: 1, properties: { [C.giftProp]:'1', gift_tier: key || '', campaign: C.campaignKey || '' } })
    });

    if (!r.ok) {
      const t = await r.text(); log('add(single) failed', r.status, t);
      r = await fetch('/cart/add.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept':'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1, properties: { [C.giftProp]:'1', gift_tier: key || '', campaign: C.campaignKey || '' } }] })
      });
      if (!r.ok) {
        addFailBackoff.set(variantId, now);
        const t2 = await r.text();
        throw new Error(`Add gift failed (${r.status}): ${t2}`);
      }
    }

    try {
      await fetch('/cart/update.js', {
        method:'POST', credentials:'same-origin', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ attributes: { campaign: C.campaignKey || 'bfcm' } })
      });
    } catch(_) {}
    return r.json();
  }

  async function enforceGiftGuard(cart) {
    if (!C.enforceGiftGuard || !cart || !Array.isArray(cart.items)) return;
    const fixes = [];
    for (const li of cart.items) {
      const isGift = li.properties && li.properties[C.giftProp] === '1';
      if (isGift && li.quantity !== 1) fixes.push(changeLine(li.key, 1));
    }
    if (fixes.length) await Promise.allSettled(fixes);
  }

  // Sections: render only if we actually changed something + have valid ids,
  // and never more frequently than maxSectionRefreshRateMs, and never for the
  // same (sig + tier) combo repeatedly.
  async function renderSectionsIfNeeded(cart, tierKey, changed) {
    if (!changed) return;
    if (!SECTIONS.length) return; // hard guard: never call "/?sections=" empty

    const now = Date.now();
    const sig = `${cartSignature(cart) || 'no'}|${tierKey || 'none'}`;
    if (sig === lastSectionsSig && now - lastSectionsAt < C.maxSectionRefreshRateMs) {
      log('skip sections: same state or within throttle window');
      return;
    }
    if (now - lastSectionsAt < C.maxSectionRefreshRateMs) {
      log('skip sections: throttle', (C.maxSectionRefreshRateMs - (now - lastSectionsAt)) + 'ms left');
      return;
    }

    const url = `${root}?sections=${encodeURIComponent(SECTIONS.join(','))}`;
    try {
      const r = await fetch(url, { credentials:'same-origin', headers:{'Accept':'application/json'} });
      if (!r.ok) return;
      const htmls = await r.json();
      for (const id of SECTIONS) {
        const el = document.getElementById(id) || document.querySelector(`[data-section-id="${id}"]`);
        if (el && htmls[id]) el.innerHTML = htmls[id];
      }
      lastSectionsSig = sig;
      lastSectionsAt = now;
    } catch(e) {
      console.warn('[BFCM] sections refresh error', e);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // 3) MAIN SYNC
  // ─────────────────────────────────────────────────────────────────────────────
  async function ensureInit(){
    if (!initPromise) initPromise = normalizeTiers().catch(e=>console.warn('[BFCM] init error', e));
    return initPromise;
  }

  async function syncGifts(hintCart){
    if (syncing) { queued = true; return; }
    syncing = true;

    let changed = false;
    let finalCart = null;
    let wantKey = null;

    try {
      await ensureInit();

      // avoid extra /cart.js if caller gives a fresh cart
      let cart = (hintCart && Array.isArray(hintCart.items)) ? hintCart : await getCart();

      const sig = cartSignature(cart);
      const amt = amountForBasis(cart);
      const wantTier = eligibleTier(amt);
      wantKey = wantTier ? (wantTier.key || String(wantTier.threshold)) : null;

      // short-circuit if absolutely nothing changed
      if (sig === lastSig && wantKey === lastTierKey) {
        log('no change; skip sync');
        finalCart = cart;
        return;
      }

      const targetId = wantTier ? String(wantTier.variantId) : null;
      const gifts = findGiftLines(cart);

      // remove unwanted gifts (or all if none eligible)
      for (const li of gifts) {
        if (!targetId || String(li.variant_id) !== targetId) {
          await changeLine(li.key, 0);
          changed = true;
        }
      }

      // add gift if desired and missing
      if (targetId && !gifts.some(li => String(li.variant_id) === targetId)) {
        await addGift(targetId, wantKey);
        changed = true;
      }

      // refresh cart if any mutation happened, enforce qty 1 for gifts
      finalCart = changed ? await getCart() : cart;
      if (changed) await enforceGiftGuard(finalCart);

      // record last state
      lastSig = cartSignature(finalCart);
      lastTierKey = wantKey;

      // emit hook for other UI (drawers, badges, etc.)
      try { document.dispatchEvent(new CustomEvent('bfcm:gifts-synced', { detail: { cart: finalCart, tier: wantKey } })); } catch(_){}

    } catch (e) {
      console.warn('[BFCM Gifts] sync error', e);
    } finally {
      try { await renderSectionsIfNeeded(finalCart || {}, wantKey, changed); } catch(_){}
      syncing = false;
      if (queued) { queued = false; syncGifts(); }
    }
  }

  // expose
  window.BFCM_syncGifts = syncGifts;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4) EVENT HOOKS — PURELY EVENT-DRIVEN (NO POLLING)
  // ─────────────────────────────────────────────────────────────────────────────
  function debounce(fn, ms){ let t; return (...a)=>{ clearTimeout(t); t = setTimeout(()=>fn.apply(null,a), ms); }; }
  const scheduleSync = debounce((cart)=>syncGifts(cart), 120);

  function wire(){
    // Theme/cart events
    document.addEventListener('cart:updated', (e)=> scheduleSync(e && e.detail && e.detail.cart));
    document.addEventListener('cart:refresh', ()=> scheduleSync());
    document.addEventListener('shopify:section:load', ()=> scheduleSync());

    // Add-to-cart forms
    document.addEventListener('submit', (e)=>{
      const f = e.target;
      if (f && (f.matches('form[action^="/cart/add"]') || f.matches('product-form form'))) {
        scheduleSync();
      }
    }, true);

    // Classic cart quantity inputs (non-AJAX themes)
    document.addEventListener('change', (e)=>{
      const el = e.target;
      if (el && el.matches('input[name^="updates"]')) scheduleSync();
    });

    // Cart fetch sniffers — only after successful cart calls
    if (!window.__BFCM_fetchPatched) {
      window.__BFCM_fetchPatched = true;
      const _f = window.fetch;
      window.fetch = async function(i, init){
        const res = await _f(i, init);
        try {
          const url = (typeof i === 'string' ? i : i && i.url) || '';
          if (/(\/cart\/(add|change|update|clear)\.js|\/cart\.js)(\?|$)/.test(String(url)) && res.ok) {
            queueMicrotask(()=> scheduleSync());
          }
        } catch(_) {}
        return res;
      };
    }
    if (!window.__BFCM_xhrPatched) {
      window.__BFCM_xhrPatched = true;
      const XO = XMLHttpRequest.prototype.open;
      const XS = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function(m,u){
        this.__bfcmCartReq = typeof u === 'string' && /(\/cart\/(add|change|update|clear)\.js|\/cart\.js)(\?|$)/.test(u);
        return XO.apply(this, arguments);
      };
      XMLHttpRequest.prototype.send = function(){
        if (this.__bfcmCartReq) {
          this.addEventListener('loadend', ()=>{
            if (this.status >= 200 && this.status < 300) queueMicrotask(()=> scheduleSync());
          });
        }
        return XS.apply(this, arguments);
      };
    }

    // One initial sync on load/restore and when tab becomes visible
    window.addEventListener('pageshow', ()=> scheduleSync());
    document.addEventListener('visibilitychange', ()=> { if (!document.hidden) scheduleSync(); });
  }

  wire();

})();
